import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vacationCompensation, type VacationCompensationItem } from '@/api/vacation';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err?.response?.data?.detail ?? err?.message ?? 'Помилка';
}

export default function VacationCompensationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // Імена працівників для активної org (для відображення ПIБ).
  const empQ = useQuery({
    queryKey: ['employees', orgId],
    queryFn: () => refs.employees(orgId, {}),
    enabled: !!orgId,
  });
  const empName = useMemo(() => {
    const m = new Map<string, string>();
    empQ.data?.forEach((e) => m.set(e.id, e.name ?? e.code ?? e.id));
    return m;
  }, [empQ.data]);

  const list = useQuery({
    queryKey: ['vacation-compensations', orgId],
    queryFn: () => vacationCompensation.list(orgId),
    enabled: !!orgId,
  });

  const postMut = useMutation({
    mutationFn: (id: string) => vacationCompensation.post(id),
    onSuccess: () => { setError(null); qc.invalidateQueries({ queryKey: ['vacation-compensations'] }); },
    onError: (e) => setError(extractError(e)),
  });
  const unpostMut = useMutation({
    mutationFn: (id: string) => vacationCompensation.unpost(id),
    onSuccess: () => { setError(null); qc.invalidateQueries({ queryKey: ['vacation-compensations'] }); },
    onError: (e) => setError(extractError(e)),
  });

  const cols: ColumnDef<VacationCompensationItem>[] = useMemo(() => [
    { key: 'document_number', header: '№', width: 110, sortValue: (r) => r.document_number, cell: (r) => r.document_number },
    { key: 'employee', header: 'Працівник', width: 200, sortValue: (r) => empName.get(r.employee_id) ?? '', cell: (r) => empName.get(r.employee_id) ?? '—' },
    { key: 'dismiss_date', header: 'Дата звільнення', width: 150, sortValue: (r) => r.dismiss_date, cell: (r) => new Date(r.dismiss_date).toLocaleDateString('uk-UA') },
    { key: 'order', header: 'Наказ', width: 110, cell: (r) => (
      <a href={`/dismissal-orders/${r.dismissal_order_id}`}
        onClick={(e) => { e.preventDefault(); navigate(`/dismissal-orders/${r.dismissal_order_id}`); }}
        style={{ color: '#2563eb', cursor: 'pointer' }}>Відкрити →</a>
    ) },
    { key: 'compensation_days', header: 'Днів', width: 80, align: 'right', sortValue: (r) => Number(r.compensation_days), cell: (r) => fmt2(r.compensation_days) },
    { key: 'average_daily_wage', header: 'Середня', width: 110, align: 'right', sortValue: (r) => Number(r.average_daily_wage), cell: (r) => fmt2(r.average_daily_wage) },
    { key: 'gross', header: 'Нараховано', width: 120, align: 'right', sortValue: (r) => Number(r.compensation_gross_amount), cell: (r) => fmt2(r.compensation_gross_amount) },
    { key: 'net_amount', header: 'До виплати', width: 120, align: 'right', sortValue: (r) => Number(r.net_amount), cell: (r) => fmt2(r.net_amount) },
    { key: 'posted', header: 'Проведено', width: 100, align: 'center', sortValue: (r) => r.posted ? 1 : 0, cell: (r) => (r.posted ? '✓ так' : 'ні') },
    {
      key: 'actions', header: 'Дія', width: 130, align: 'center', cell: (r) => (
        r.posted ? (
          <button onClick={() => unpostMut.mutate(r.id)} disabled={unpostMut.isPending}>
            Розпровести
          </button>
        ) : (
          <button className="primary" onClick={() => postMut.mutate(r.id)} disabled={postMut.isPending}>
            Провести
          </button>
        )
      ),
    },
  ], [empName, postMut, unpostMut]);

  return (
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end' }}>
        <h2 style={{ margin: 0 }}>Компенсації відпустки при звільненні</h2>
        <div className="field" style={{ minWidth: 220, margin: 0 }}>
          <label>Організація *</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">— оберіть —</option>
            {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.code} — {o.name}</option>)}
          </select>
        </div>
        <button className="primary" style={{ marginLeft: 'auto' }}
          title="Створити нарахування компенсації (оберіть наказ про звільнення)"
          onClick={() => navigate('/hr/vacation-compensations/new')}>
          + Нове нарахування
        </button>
      </div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Компенсація створюється з наказу звільнення (авто-чернетка при проведенні або
        кнопкою «Розрахувати компенсацію» у наказі). Дабл-клік по рядку — відкрити
        чернетку для редагування (дні/середня → перерахунок) і проведення.
      </p>

      {error && <div style={{ background: '#fee', color: '#b00', padding: '0.4rem 0.6rem', borderRadius: 6, marginBottom: '0.5rem' }}>{error}</div>}

      {orgId ? (
        <DataTable<VacationCompensationItem>
          rows={list.data ?? []}
          columns={cols}
          rowKey={(r) => r.id}
          loading={list.isLoading}
          emptyText="Немає розрахунків компенсації."
          onRowDoubleClick={(r) => navigate(`/hr/vacation-compensations/${r.id}`)}
          rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
          initialSortKey="dismiss_date"
          initialSortDir="desc"
        />
      ) : (
        <p style={{ color: 'var(--muted)' }}>Оберіть організацію.</p>
      )}
    </div>
  );
}
